import ArrowButton from '@/components/Record/SubjectContent/ArrowButton';
import React, { useEffect, useRef, useState } from 'react';
import AttenderItem from '@/components/Record/SubjectContent/AttenderItem';
import { usePassportSearch } from '@/hooks/apiHooks';
import AttenderInput from '@/components/Record/SubjectContent/AttenderInput';
import { useFormContext } from 'react-hook-form';
import { useDebounce } from 'usehooks-ts';
import SearchList from '@/components/Record/SubjectContent/SearchList';
import useClickOutside from '../@hooks/useClickOutside';
import { InternalParticipant, RecordFilter } from '@/types/api';
import { useTranslation } from 'react-i18next';
import { useAttenderKeyboard } from '@/components/Record/SubjectContent/@hooks/useAttenderKeyboard';

const registerName = 'searchFields.innerAttender';

const InnerAttender = () => {
  const { t } = useTranslation();
  const [isFocus, setIsFocus] = useState(false);
  const [searchFocusIndex, setSearchFocusIndex] = useState(-1);
  const [attenderFocusIndex, setAttenderFocusIndex] = useState(-1);
  const { watch, getValues, setValue } = useFormContext<RecordFilter>();

  const innerAttender = watch('internal');
  const ref = useRef<HTMLDivElement>(null);

  const searchString = watch(registerName);
  const debouncedValue = useDebounce(searchString, 500);

  const { data, isLoading } = usePassportSearch({ keyword: debouncedValue }, 2);

  const searchData =
    data?.data?.map((res) => ({
      passportId: res.passportId,
      name: res.detail.name,
      departmentName: res.detail.departmentName,
      positionName: res.detail.positionName,
      divisionName: res.detail.divisionName,
      profileImageUrl: res.detail.profileImageUrl,
    })) || [];

  const handleDelete = (passportId: string) => {
    const current = getValues('internal');
    const updated = current.filter(
      (item: InternalParticipant) => item.passportId !== passportId,
    );
    setValue('internal', updated);
    // Reset attender focus if item was deleted
    if (attenderFocusIndex >= updated.length) {
      setAttenderFocusIndex(Math.max(-1, updated.length - 1));
    }
  };

  const handleTogglePassport = (passport: InternalParticipant) => {
    const current = getValues('internal');
    const exists = current.some(
      (item) => item.passportId === passport.passportId,
    );
    const updated = exists
      ? current.filter((item) => item.passportId !== passport.passportId)
      : [...current, passport];
    setValue('internal', updated);
    setValue(registerName, '');
  };

  const { handleKeyDown } = useAttenderKeyboard({
    isFocus,
    debouncedValue,
    searchString,
    searchData,
    attenderList: innerAttender,
    searchFocusIndex,
    attenderFocusIndex,
    registerName,
    setSearchFocusIndex,
    setAttenderFocusIndex,
    setIsFocus,
    setValue,
    handleTogglePassport,
    handleDelete,
  });

  // Reset search focus when search data changes
  useEffect(() => {
    setSearchFocusIndex(-1);
  }, [debouncedValue]);

  // Reset attender focus when input has content
  useEffect(() => {
    if (searchString.length > 0) {
      setAttenderFocusIndex(-1);
    }
  }, [searchString]);

  useClickOutside(ref, () => {
    setIsFocus(false);
    setValue('searchFields.innerAttender', '');
  });

  return (
    <>
      <div ref={ref}>
        <AttenderInput
          isFocus={isFocus}
          placeholder={t('어떤 분이 회의에 참여하였나요?')}
          registerName={registerName}
          handleKeyDown={handleKeyDown}
          leftChildren={innerAttender.map((item, index) => (
            <AttenderItem
              key={`passport-${item.passportId}`}
              data={{
                passportId: item.passportId,
                name: item.name,
                departmentName: item.departmentName,
                positionName: item.positionName,
                divisionName: item.divisionName,
                profileImageUrl: item.profileImageUrl,
              }}
              handleDelete={handleDelete}
              isFocused={attenderFocusIndex === index}
            />
          ))}
          rightChildren={
            <ArrowButton onOpen={() => setIsFocus(!isFocus)} open={isFocus} />
          }
          onFocus={() => setIsFocus(true)}
        />
        {isFocus && (
          <SearchList
            registerKey={'internal'}
            open={debouncedValue.length > 0}
            data={searchData}
            focusIndex={searchFocusIndex}
            onToggle={handleTogglePassport}
            searchKeyword={debouncedValue}
          />
        )}
      </div>
    </>
  );
};

export default InnerAttender;
